<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import MdiPackageVariant from '~icons/mdi/package-variant'
import MdiRocketLaunch from '~icons/mdi/rocket-launch'
import MdiClose from '~icons/mdi/close'
import MdiLockOutline from '~icons/mdi/lock-outline'
import MdiLogout from '~icons/mdi/logout'
import MdiPlus from '~icons/mdi/plus'
import { logOutApi, getFrontpageDashboard, getMacro, deleteFrontpageCard, reorderFrontpageCards } from '@/api/api.js'
import { useStore } from '@/stores/store'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions.js'
import { useShellModeStore } from '@/stores/shellMode.js'
import Skeleton from '@/components/common/Skeleton.vue'
import RunMacroModal from '@/components/macros/RunMacroModal.vue'
import OnboardingWizard from '@/components/OnboardingWizard.vue'
import FrontpageTile from '@/components/FrontpageTile.vue'
import FrontpageEditModal from '@/components/FrontpageEditModal.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import { useToast } from '@/composables/useToast.js'

const store = useStore()
const authStore = useAuthStore()
const shell = useShellModeStore()
const toast = useToast()
const { t } = useI18n()
const { isAdmin } = usePermissions()
shell.preferForRole()

const canEditFrontpage = computed(() => isAdmin.value && shell.isOffice)

const period = ref(14)
const dashboard = ref(null)
const chartError = ref('')
const chartLoading = ref(false)
let chartRequest = 0
async function loadDashboard() {
  const generation = ++chartRequest
  dashboard.value = null
  chartLoading.value = true
  chartError.value = ''
  const start = new Date()
  start.setUTCHours(0, 0, 0, 0)
  const end = new Date(start.getTime() + period.value * 86400000)
  try {
    const result = await getFrontpageDashboard(start.toISOString(), end.toISOString())
    if (generation === chartRequest) dashboard.value = result
  } catch {
    if (generation === chartRequest) chartError.value = t('ui.couldNotLoadOperationalData')
  } finally {
    if (generation === chartRequest) chartLoading.value = false
  }
}
watch([period, () => authStore.currentTenant?.slug], loadDashboard, { immediate: true })
watch(() => authStore.currentTenant?.slug, () => {
  store.fetchFrontpage(true)
  editing.value = false
})
const isLoading = computed(() => store.isLoadingFrontpage)

const showNoAccess = computed(() =>
  store.locationsFetched &&
  !store.isLoadingLocations &&
  authStore.permissions !== null &&
  store.locations.length === 0 &&
  !isAdmin.value
)

const sections = computed(() => {
  const map = new Map()
  for (const item of store.startpage) {
    const key = item.section || t('home.overview')
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(item)
  }
  return map
})

onMounted(() => {
  if (!store.startpage.length && !store.isLoadingFrontpage) {
    store.fetchFrontpage()
  }
  if (store.locationsFetched && !store.isLoadingLocations) {
    checkOnboarding()
  }
})

// ── Onboarding wizard ─────────────────────────────────────────────────────────

const showWizard = ref(false)
const showBanner = ref(false)

const onboardingAutoOpenedKey = computed(() => {
  const slug = authStore.currentTenant?.slug
  return slug ? `onboarding_auto_opened_${slug}` : null
})

const onboardingDismissedKey = computed(() => {
  const slug = authStore.currentTenant?.slug
  return slug ? `onboarding_dismissed_${slug}` : null
})

function checkOnboarding() {
  if (!store.locationsFetched || store.isLoadingLocations) return
  if (store.locations.length > 0) return
  if (authStore.permissions === null) return
  if (!isAdmin.value) return

  const dismissedKey = onboardingDismissedKey.value
  const autoOpenedKey = onboardingAutoOpenedKey.value

  if (dismissedKey && localStorage.getItem(dismissedKey)) return

  if (autoOpenedKey && !localStorage.getItem(autoOpenedKey)) {
    localStorage.setItem(autoOpenedKey, '1')
    showWizard.value = true
  } else {
    showBanner.value = true
  }
}

watch(() => store.isLoadingLocations, (loading) => {
  if (!loading) checkOnboarding()
})

watch(() => authStore.permissions, (perms) => {
  if (perms !== null) checkOnboarding()
})

function dismissBanner() {
  if (onboardingDismissedKey.value) {
    localStorage.setItem(onboardingDismissedKey.value, '1')
  }
  showBanner.value = false
}

function openWizard() {
  showBanner.value = false
  showWizard.value = true
}

function onWizardClose() {
  showWizard.value = false
  if (store.locations.length > 0) {
    showBanner.value = false
  } else if (!onboardingDismissedKey.value || !localStorage.getItem(onboardingDismissedKey.value)) {
    showBanner.value = true
  }
}

// ── Macro run modal ───────────────────────────────────────────────────────────

const selectedMacro = ref(null)
const showRunModal = ref(false)

function parseMacroIdFromLink(link) {
  if (!link || typeof link !== 'string') return null
  const idInPath = link.match(/\/macro\/(\d+)(?:\/|$)/i) || link.match(/\/admin\/macros\/(\d+)(?:\/|$)/i)
  if (idInPath) return Number(idInPath[1])
  const idInQuery = link.match(/[?&]macro_id=(\d+)/i) || link.match(/[?&]id=(\d+)/i)
  if (idInQuery) return Number(idInQuery[1])
  return null
}

function resolveMacroId(element) {
  const candidates = [
    element?.macro_id,
    element?.macro?.id,
    element?.macro,
    parseMacroIdFromLink(element?.link),
  ]

  if (Array.isArray(element?.inputs) || (element?.inputs && typeof element.inputs === 'object')) {
    candidates.push(element?.id)
  }

  for (const c of candidates) {
    const n = Number(c)
    if (Number.isInteger(n) && n > 0) return n
  }
  return null
}

async function openMacroRun(element) {
  const macroId = resolveMacroId(element)
  if (!macroId) {
    if (Array.isArray(element?.inputs) || (element?.inputs && typeof element.inputs === 'object')) {
      selectedMacro.value = element
      showRunModal.value = true
      return
    }
    toast.error(t('home.loadMacroError'))
    return
  }

  try {
    const macro = await getMacro(macroId)
    selectedMacro.value = macro
    showRunModal.value = true
  } catch {
    if (Array.isArray(element?.inputs) || (element?.inputs && typeof element.inputs === 'object')) {
      selectedMacro.value = element
      showRunModal.value = true
      return
    }
    toast.error(t('home.loadMacroFailed'))
  }
}

function closeMacroRun() {
  showRunModal.value = false
  selectedMacro.value = null
}

// ── Frontpage edit mode ───────────────────────────────────────────────────────

const editing = ref(false)
const busy = ref(false)
const editCard = ref(null)
const showEditModal = ref(false)
const deleteTarget = ref(null)
const dragSrcId = ref(null)

function toggleEditing() {
  editing.value = !editing.value
  if (!editing.value) {
    showEditModal.value = false
    editCard.value = null
    deleteTarget.value = null
    dragSrcId.value = null
  }
}

function openAddCard() {
  editCard.value = null
  showEditModal.value = true
}

function openEditCard(card) {
  editCard.value = card
  showEditModal.value = true
}

function closeEditModal() {
  showEditModal.value = false
  editCard.value = null
}

async function onCardSaved() {
  closeEditModal()
  await store.fetchFrontpage(true)
}

function requestDelete(card) {
  deleteTarget.value = card
}

async function confirmDelete() {
  if (!deleteTarget.value) return
  busy.value = true
  try {
    await deleteFrontpageCard(deleteTarget.value.id)
    deleteTarget.value = null
    await store.fetchFrontpage(true)
  } catch {
    toast.error(t('ui.couldNotDeleteTheCardTryAgain'))
  } finally {
    busy.value = false
  }
}

function onDragStart(card) {
  dragSrcId.value = card.id
}

function onDragOver(card) {
  if (dragSrcId.value === null || dragSrcId.value === card.id) return
  const cards = [...store.startpage]
  const from = cards.findIndex((c) => c.id === dragSrcId.value)
  const to = cards.findIndex((c) => c.id === card.id)
  if (from < 0 || to < 0) return
  const [moved] = cards.splice(from, 1)
  cards.splice(to, 0, moved)
  store.startpage = cards
  dragSrcId.value = card.id
}

async function onDragEnd() {
  dragSrcId.value = null
  if (!editing.value) return
  busy.value = true
  try {
    await reorderFrontpageCards(store.startpage.map((c) => c.id))
    await store.fetchFrontpage(true)
  } catch {
    toast.error(t('ui.couldNotReorderCardsTryAgain'))
    await store.fetchFrontpage(true)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <OnboardingWizard v-if="showWizard" @close="onWizardClose" />

  <div v-if="showNoAccess" class="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center gap-4">
    <div class="flex items-center justify-center w-16 h-16 rounded-2xl"
         style="background: rgba(255,255,255,0.05); border: 1px solid var(--color-border);">
      <MdiLockOutline class="w-8 h-8" style="color: var(--color-muted);" />
    </div>
    <div>
      <p class="text-base font-semibold" style="color: var(--color-text);">{{ $t('home.noAccess') }}</p>
      <p class="text-sm mt-1" style="color: var(--color-muted);">{{ $t('home.noAccessDesc') }}</p>
      <p class="text-sm mt-0.5" style="color: var(--color-muted);">{{ $t('ux.noAccessRequest') }}</p>
    </div>
    <div class="flex flex-wrap items-center justify-center gap-2 mt-2">
      <router-link to="/tenant" class="btn secondary small">{{ $t('ux.switchTenant') }}</router-link>
      <button class="btn ghost small flex items-center gap-2" @click="logOutApi()">
        <MdiLogout class="w-4 h-4" />
        {{ $t('common.logOut') }}
      </button>
    </div>
  </div>

  <div v-else class="max-w-6xl mx-auto px-4 pt-4 pb-4 space-y-6">

    <section v-if="shell.isWarehouse" class="rounded-2xl p-4 space-y-3" style="background: rgba(168,208,193,0.06); border: 1px solid rgba(168,208,193,0.18);">
      <h2 class="text-sm font-semibold tracking-wide uppercase" style="color: var(--color-accent);">{{ $t('ux.missionBoard') }}</h2>
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <router-link to="/checkout/" class="btn secondary small justify-center">{{ $t('ux.missionCheckout') }}</router-link>
        <router-link to="/return" class="btn secondary small justify-center">{{ $t('ux.missionReturn') }}</router-link>
        <router-link to="/transfers" class="btn secondary small justify-center">{{ $t('ux.missionTransfers') }}</router-link>
        <router-link to="/projects" class="btn secondary small justify-center">{{ $t('ux.missionPick') }}</router-link>
      </div>
    </section>

    <div
      v-if="canEditFrontpage || store.startpage.some(card => card.content_type === 'chart')"
      class="fp-toolbar"
    >
      <div
        v-if="store.startpage.some(card => card.content_type === 'chart')"
        class="fp-period"
        role="group"
        :aria-label="$t('ui.dashboardPeriodUtc')"
      >
        <span class="fp-period__label">{{ $t('ui.dashboardPeriodUtc') }}</span>
        <div class="fp-period__seg">
          <button
            type="button"
            class="fp-period__btn"
            :class="{ 'fp-period__btn--active': period === 1 }"
            :aria-pressed="period === 1"
            @click="period = 1"
          >{{ $t('common.today') }}</button>
          <button
            type="button"
            class="fp-period__btn"
            :class="{ 'fp-period__btn--active': period === 7 }"
            :aria-pressed="period === 7"
            @click="period = 7"
          >{{ $t('ui.next7Days') }}</button>
          <button
            type="button"
            class="fp-period__btn"
            :class="{ 'fp-period__btn--active': period === 14 }"
            :aria-pressed="period === 14"
            @click="period = 14"
          >{{ $t('ui.next14Days') }}</button>
        </div>
      </div>
      <button
        v-if="canEditFrontpage"
        type="button"
        class="btn small"
        :disabled="busy"
        @click="toggleEditing"
      >
        {{ editing ? $t('common.done') : $t('common.edit') }}
      </button>
    </div>
    <p v-if="store.frontpageError" role="alert">{{ $t('ui.couldNotLoadTheFrontPage') }} <button class="btn" @click="store.fetchFrontpage(true)">{{ $t('common.retry') }}</button></p>

    <div
      v-if="showBanner && !showWizard"
      class="rounded-2xl p-4 flex items-center gap-4 backdrop-blur-xl"
      style="background: rgba(168,208,193,0.08); border: 1px solid rgba(168,208,193,0.2);"
    >
      <div class="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full"
           style="background: rgba(168,208,193,0.12);">
        <MdiRocketLaunch class="w-5 h-5" style="color: var(--color-accent);" />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold leading-tight" style="color: var(--color-text);">{{ $t('home.setupWorkspace') }}</p>
        <p class="text-xs mt-0.5" style="color: var(--color-muted);">{{ $t('home.setupDesc') }}</p>
      </div>
      <button class="btn primary small shrink-0" @click="openWizard">{{ $t('common.getStarted') }}</button>
      <button class="btn-icon shrink-0" :title="$t('common.dismiss')" @click="dismissBanner">
        <MdiClose class="w-4 h-4" />
      </button>
    </div>

    <section v-if="isLoading">
      <h2 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{{ $t('home.overview') }}</h2>
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Skeleton type="card" v-for="i in 4" :key="i" class="h-20 rounded-2xl" />
      </div>
    </section>

    <template v-else-if="store.startpage && store.startpage.length">
      <section v-for="[sectionName, items] in sections" :key="sectionName">
        <h2 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{{ sectionName }}</h2>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <FrontpageTile
            v-for="element in items"
            :key="element.id"
            :element="element"
            :editing="editing"
            :dragging="dragSrcId === element.id"
            :dashboard="dashboard"
            :chart-loading="chartLoading"
            :chart-error="chartError"
            @activate="openMacroRun"
            @edit="openEditCard"
            @delete="requestDelete"
            @retry="loadDashboard"
            @dragstart="onDragStart(element)"
            @dragover="onDragOver(element)"
            @dragend="onDragEnd"
          />
        </div>
      </section>
      <div v-if="editing" class="flex justify-center pt-2">
        <button type="button" class="fp-tile-add" style="min-height: 3.5rem; width: 100%; max-width: 16rem;" :disabled="busy" @click="openAddCard">
          <MdiPlus class="w-6 h-6" />
          <span class="text-xs">{{ $t('ui.addCard') }}</span>
        </button>
      </div>
    </template>

    <section v-else class="text-center py-12">
      <div class="mb-3 flex justify-center">
        <MdiPackageVariant class="w-10 h-10 text-gray-500" />
      </div>
      <p class="text-gray-400 text-sm">{{ $t('ui.noDashboardDataYet') }}</p>
      <p class="text-gray-500 text-xs mt-1">{{ $t('ui.makeSureAWarehouseIsConfiguredInTheBackend') }}</p>
      <button
        v-if="canEditFrontpage"
        type="button"
        class="btn primary small mt-4"
        @click="editing = true; openAddCard()"
      >{{ $t('ui.addCard') }}</button>
    </section>

  </div>

  <RunMacroModal :show="showRunModal" :macro="selectedMacro" @close="closeMacroRun" />
  <FrontpageEditModal
    :show="showEditModal"
    :card="editCard"
    @saved="onCardSaved"
    @cancel="closeEditModal"
  />
  <BaseConfirmModal
    :show="!!deleteTarget"
    :title="$t('common.delete')"
    :body="deleteTarget ? $t('ui.deleteCardConfirm', { title: deleteTarget.title }) : ''"
    :loading="busy"
    @confirm="confirmDelete"
    @cancel="deleteTarget = null"
  />
</template>
